import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Sparkles, Star, ChevronRight, Film, Zap, Baby, User} from 'lucide-react'
import { SearchBar } from '../components/SearchBar'
import { Newsletter } from '../components/Newsletter'
import { UserAuth } from '../components/UserAuth'
import { useAuth } from '../context/AuthContext'

export function HomePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)

  const handleSearch = (query: string) => {
    navigate(`/search?q=${encodeURIComponent(query)}`)
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-sm shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-pink-500" />
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pink-500 via-orange-500 to-teal-500 bg-clip-text text-transparent">
                MagicCartoon.com
              </h1>
            </Link>
            <div className="flex items-center gap-4">
              <Link 
                to="/category/kids" 
                className="text-gray-600 hover:text-pink-500 font-semibold transition-colors hidden md:inline"
              >
                Kids
              </Link>
              <Link 
                to="/category/classics" 
                className="text-gray-600 hover:text-pink-500 font-semibold transition-colors hidden md:inline"
              >
                Classics
              </Link>
              <Link 
                to="/category/modern" 
                className="text-gray-600 hover:text-pink-500 font-semibold transition-colors hidden md:inline"
              >
                Modern
              </Link>
              <Link 
                to="/search" 
                className="text-gray-600 hover:text-pink-500 font-semibold transition-colors"
              >
                Search
              </Link>
              {user ? (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white px-4 py-2 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  <img src={user.avatar} alt={user.username} className="w-6 h-6 rounded-full" />
                  <span className="hidden sm:inline">{user.username}</span>
                </Link>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white px-4 py-2 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden sm:inline">Login</span>
                </button>
              )}
              <Star className="w-6 h-6 text-yellow-400 animate-pulse" />
            </div>
          </div>
        </div>
      </nav>

      {/* Auth Modal */}
      <UserAuth isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-200 via-orange-200 to-teal-200 opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center mb-8">
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-800 mb-6 leading-tight">
              Discover the Best <span className="text-pink-500">Magic Cartoon</span> Moments
            </h2>
            <p className="text-xl sm:text-2xl text-gray-700 max-w-3xl mx-auto mb-8">
              From classic spells to modern miracles, explore the world of animated fantasy.
            </p>
            <div className="max-w-2xl mx-auto">
              <SearchBar onSearch={handleSearch} />
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl max-w-5xl mx-auto transform hover:scale-[1.02] transition-transform duration-300">
            <img 
              src="https://static.lumi.new/06/06ed54a85c6788b0c73822601ed1a66c.png" 
              alt="Magical cartoon moments with sparkles and stars"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Featured Review - Tom & Jerry */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-gradient-to-r from-pink-500 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
              FAN FAVORITE
            </span>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Tom & Jerry Magic Ring
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 hover:rotate-1 transition-all duration-300">
              <img 
                src="https://static.lumi.new/67/678dc25a595a0cdc8d11f06c2bbd13e0.png" 
                alt="Tom and Jerry chasing a magical ring"
                className="w-full h-auto"
              />
            </div>
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                Read our deep dive into the hilarious chaos of <strong>Tom and Jerry Magic</strong>. 
                Find out why the <strong>Tom and Jerry Ring</strong> chase is the ultimate cat and mouse fantasy adventure.
              </p>
              <p className="text-gray-600">
                This classic animated film combines the timeless rivalry with magical mayhem, 
                creating unforgettable moments that have delighted families for generations.
              </p>
              <Link 
                to="/review/tom-jerry-magic-ring"
                className="group bg-gradient-to-r from-pink-500 to-orange-500 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2 w-fit"
              >
                Read More
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Classic Hits - Smurfs */}
      <section className="bg-gradient-to-br from-blue-50 to-green-50 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-gradient-to-r from-blue-500 to-green-500 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
              RETRO CLASSICS
            </span>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              The Smurfs Magic Flute
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                Relive the adventure of <strong>The Smurfs and the Magic Flute</strong>. 
                A timeless tale that defined a generation.
              </p>
              <p className="text-gray-600">
                Journey back to the enchanted forest where tiny blue heroes embark on a musical quest. 
                This vintage gem captures the heart and whimsy of classic European animation.
              </p>
              <Link 
                to="/review/smurfs-magic-flute"
                className="group bg-gradient-to-r from-blue-500 to-green-500 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2 w-fit"
              >
                Read More
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="order-1 md:order-2 rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 hover:-rotate-1 transition-all duration-300">
              <img 
                src="https://static.lumi.new/87/8717cfaa1cc47f76a788c90416e49295.png" 
                alt="The Smurfs with magical flute in mushroom village"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* For Kids - Morphle */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
              FOR KIDS
            </span>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Watch Cartoon Morphle
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 hover:rotate-1 transition-all duration-300">
              <img 
                src="https://static.lumi.new/d8/d859fdb4778979e3124cacdf655830a3.png" 
                alt="Morphle the magical red shape-shifting pet"
                className="w-full h-auto"
              />
            </div>
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                Meet the magical red pet! Explore our guide to every <strong>Morphle Cartoon</strong> episode 
                and character transformation.
              </p>
              <p className="text-gray-600">
                Perfect for young viewers, <strong>Cartoon Morphle</strong> brings shape-shifting adventures 
                and colorful fun that sparks imagination and creativity in children everywhere.
              </p>
              <Link 
                to="/review/morphle-cartoon"
                className="group bg-gradient-to-r from-red-500 to-pink-500 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2 w-fit"
              >
                Read More
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by Category */}
      <section className="bg-gradient-to-br from-purple-50 to-pink-50 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Browse by Category
            </h3>
            <p className="text-lg text-gray-700">
              Explore our curated collections of magical cartoons
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Kids Category */}
            <Link
              to="/category/kids"
              className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 text-center group"
            >
              <Baby className="w-16 h-16 text-pink-500 mx-auto mb-4 group-hover:animate-bounce" />
              <h4 className="text-2xl font-bold text-gray-800 mb-2">Kids</h4>
              <p className="text-gray-600 mb-4">
                Safe, fun magical adventures perfect for young viewers
              </p>
              <span className="text-pink-500 font-semibold group-hover:underline">
                Explore Kids →
              </span>
            </Link>

            {/* Classics Category */}
            <Link
              to="/category/classics"
              className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 text-center group"
            >
              <Film className="w-16 h-16 text-amber-500 mx-auto mb-4 group-hover:animate-pulse" />
              <h4 className="text-2xl font-bold text-gray-800 mb-2">Classics</h4>
              <p className="text-gray-600 mb-4">
                Timeless tales from the golden age of animation
              </p>
              <span className="text-amber-500 font-semibold group-hover:underline">
                Explore Classics →
              </span>
            </Link>

            {/* Modern Category */}
            <Link
              to="/category/modern"
              className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 text-center group"
            >
              <Zap className="w-16 h-16 text-cyan-500 mx-auto mb-4 group-hover:animate-bounce" />
              <h4 className="text-2xl font-bold text-gray-800 mb-2">Modern</h4>
              <p className="text-gray-600 mb-4">
                Contemporary animated series with innovative magic
              </p>
              <span className="text-cyan-500 font-semibold group-hover:underline">
                Explore Modern →
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Hidden Gems */}
      <section className="bg-gradient-to-br from-yellow-50 to-orange-50 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
              HIDDEN GEMS
            </span>
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Magical Masterpieces
            </h3>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Don't miss the legendary <strong>Magical Maestro</strong> or the thrilling adventure of 
              <strong> Sinbad and His Magic Belt</strong>.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Magical Maestro */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-all duration-300">
              <img 
                src="https://static.lumi.new/5c/5c4ca435178b44ad325564f132b0826e.png" 
                alt="Magical Maestro vintage cartoon scene"
                className="w-full h-64 object-cover"
              />
              <div className="p-6 space-y-4">
                <h4 className="text-2xl font-bold text-gray-800">Magical Maestro</h4>
                <p className="text-gray-600">
                  A golden age animation masterpiece featuring theatrical magic and musical mayhem. 
                  This vintage gem showcases the artistry of classic cartoon wizardry.
                </p>
                <button className="group bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2">
                  Read More
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Sinbad */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-all duration-300">
              <img 
                src="https://static.lumi.new/38/38c45e0c24502fee403f9873841cacc6.png" 
                alt="Sinbad and His Magic Belt adventure scene"
                className="w-full h-64 object-cover"
              />
              <div className="p-6 space-y-4">
                <h4 className="text-2xl font-bold text-gray-800">Sinbad and His Magic Belt</h4>
                <p className="text-gray-600">
                  Embark on an Arabian Nights adventure filled with mystical artifacts and daring quests. 
                  A treasure of retro animation storytelling.
                </p>
                <button className="group bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center gap-2">
                  Read More
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-pink-400" />
            <h2 className="text-2xl font-bold">MagicCartoon.com</h2>
          </div>
          <p className="text-gray-400 mb-4">
            Your ultimate guide to the most magical moments in animation history
          </p>
          <p className="text-sm text-gray-500">
            © 2024 MagicCartoon.com - All Rights Reserved
          </p>
        </div>
      </footer>
    </div>
  )
}
